import {faExclamationCircle} from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import {StoreMixin} from '@teemill/components/mixins';
import {hsvToHex, formatHex, hexToHsv, hsvToRgb} from '@teemill/utilities';
import {Manufacturer} from '@teemill/common/models';
import OmniImage from '../../omnicanvas/objects/image';
import {SierraCanvas} from '@teemill/common/classes';
import {isBase64} from '@teemill/common/helpers';
import {snackbar} from '@teemill/common/services';

import {overlays} from '@teemill/modules/overlays';

export default {
  mixins: [StoreMixin],

  props: {
    /**
     * Provides some default options for the initialisation of Studio.
     * Valid options are sku, colour and designFile
     *
     * @type {Object}
     */
    defaultOptions: {
      type: Object,
      default: null,
    },

    designUrl: {
      type: String,
    },

    backDesignUrl: {
      type: String,
    },

    config: {
      type: Object,
      required: true,
    },

    urlPrefix: {
      type: String,
      default: '/omnis/v3/studio/',
    },

    designScalingMethod: {
      type: String,
      default: 'relative',
    },
  },

  data() {
    return {
      canvas: null,
      registeredVuexModule: false,
      minZoom: 1,
      maxZoom: 5,
      isZoomedIn: false,

      showGetStarted: null,

      resizeListeners: [],

      filterableImage: null,

      _isMobile: false,
      _colourFilterTimeout: null,
      overlays,
    };
  },

  computed: {
    devMode() {
      return import.meta.env.DEV;
    },

    getContainerClassList() {
      if (!this.registeredVuexModule) {
        return [];
      }
      return {
        'slide-open': this.getVuexProperty('slideOpen'),
        'slide-closing':
          !this.getVuexProperty('activeTab') &&
          this.getVuexProperty('slideClosing'),
      };
    },

    internalUser() {
      return this.$store.state.user.internal;
    },
  },

  provide() {
    return {
      setVuexProperty: this.setVuexProperty,
      getVuexProperty: this.getVuexProperty,
      isMobile: this.isMobile,
      createImageElement: this.createImageElement,
      getCanvas: this.getCanvas,
      addResizeListener: this.addResizeListener,
      getStudioComponentHeight: this.getStudioComponentHeight,
      getNavHeight: this.getNavHeight,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      config: this.config,
      openTooltipOnce: this.openTooltipOnce,
      openTooltipOnceIfDoesNotHaveBadge: this.openTooltipOnceIfDoesNotHaveBadge,
      openTooltipOnceIfHasBadge: this.openTooltipOnceIfHasBadge,
      urlPrefix: this.urlPrefix,
      warnAboutImageSize: this.warnAboutImageSize,
    };
  },

  methods: {
    /**
     * Updates the check to see if the current browser is in a mobile view
     *
     * @return {void}
     */
    getIsMobile() {
      this._isMobile = window.innerWidth < 992;
    },

    /**
     * Add a new event handler for when the window is resized
     *
     * @param {Function} method The callback function
     * @return {void}
     */
    addResizeListener(method) {
      this.resizeListeners.push(method);
    },

    /**
     * Check the cached mobile test. (Required to be reactive)
     *
     * @return {Boolean}
     */
    isMobile() {
      return this._isMobile;
    },

    /**
     * Event handler for when a tab is selected
     *
     * @return {void}
     */
    tabSelected(tab) {
      if (tab?.tags.includes('design')) {
        this.showGetStarted = false;
      }
    },

    /**
     * Event handler for an event that is triggered when the OmniCanvas is
     * initialised
     *
     * @param {OmniCanvas} cnv The new OmniCanvas instance
     * @return {void}
     */
    canvasInitialised(cnv) {
      this.canvas = cnv;

      cnv.eventBus.once('element-added', () => (this.showGetStarted = false));
    },

    afterCanvasInitialised() {
      if (this.registeredVuexModule) {
        this.initialise();
      }
    },

    canvasDestroyed() {
      this.canvas = null;
    },

    /**
     * Returns the active OmniCanvas instance
     *
     * @return {OmniCanvas}
     */
    getCanvas() {
      return this.canvas;
    },

    /**
     * Finds the currently selected element in the canvas, if any are selected
     *
     * @return {OmniObject}
     */
    getActiveElement() {
      return this.getCanvas().activeEl;
    },

    /**
     * Create an image element and add it to canvas
     *
     * @param {Image} img The image element to be used as the basis for the new
     * OmniImage object
     * @param {Object} options The options for the new OmniImage object
     * @return {void}
     */
    createImageElement(
      image,
      options = {},
      skipResize = false,
      targetScale = 6
    ) {
      options.sourceUrl = image.src;

      if (isBase64(image.src)) {
        this.onBase64Uploaded();
      }

      if (skipResize) {
        this.createOmniCanvasElementFromImage(image, options);
      } else {
        this.resizeImageElement(image, targetScale).then(resizedImage => {
          this.createOmniCanvasElementFromImage(resizedImage, options);
        });
      }
    },

    // abstract function to be overriden in components that utilise this mixin
    onBase64Uploaded() {
      // noop
    },

    createOmniCanvasElementFromImage(img, options) {
      // if (img.nodeName === 'IMG') {
      //   if (!img.src || img.src.slice(0, 14) !== 'data:image/svg') {
      //     if (img.width * img.height < 1000000) {
      //       this.$dialogs.showSnackBar({
      //         icon: faExclamationCircle,
      //         message:
      //           'This image may appear blurry when printed. For best results upload images that are at least 2000x2000 pixels or SVGs',
      //         timeout: 8000,
      //       });
      //     }
      //   }
      // }
      let defaultOptions = {
        img,
        width: img.width,
        height: img.height,
        mipMapsEnabled: false,
        filters: {},
      };
      if (options) {
        defaultOptions = Object.assign(defaultOptions, options);
      }
      const imageElement = new OmniImage(defaultOptions);

      const cnv = this.getCanvas();

      if (!cnv || !cnv.renderer) {
        return;
      }

      const element = cnv.insert(imageElement);

      cnv.params.rendering = true;
      imageElement.mipMapsEnabled = false;

      if (!defaultOptions.position) {
        cnv.fillAndMoveToHeight(element, 0.25);
        cnv.replaceHistoryLog();
      } else if (!defaultOptions.width) {
        cnv.fill(element);
        cnv.render();
      }

      cnv.params.rendering = false;
      cnv.render();

      if (img instanceof Image) {
        if (img.complete) {
          imageElement.getImageMetaData();
        } else {
          img.onload = () => {
            imageElement.getImageMetaData();
          };
        }
      } else {
        imageElement.getImageMetaData();
      }

      this.showGetStarted = false;
    },

    resizeImageElement(img, scale = 6) {
      return new Promise(resolve => {
        const bounds = this.getCanvas()?.boundingBox;

        if (!bounds?.width || !bounds?.height) {
          resolve(img);
          return;
        }

        const boundsWidth = bounds.width * scale;
        const boundsHeight = bounds.height * scale;

        const heightRatio = img.height / boundsHeight;
        const widthRatio = img.width / boundsWidth;

        const cnv = new SierraCanvas();
        cnv.resizeToImage(img);

        const type =
          img.src.slice(0, 14) === 'data:image/jpeg'
            ? 'image/jpeg'
            : 'image/webp';

        if (boundsWidth > img.width && boundsHeight > img.height) {
          cnv.drawImage(img, 0, 0, cnv.width, cnv.height);

          cnv.toImage(type, 0.92).then(img => {
            cnv.destroy();
            resolve(img);
          });
          return;
        }

        if (heightRatio <= widthRatio) {
          cnv.resizeCanvas(boundsWidth, img.height / (img.width / boundsWidth));
        } else {
          cnv.resizeCanvas(
            img.width / (img.height / boundsHeight),
            boundsHeight
          );
        }

        cnv.drawImage(img, 0, 0, cnv.width, cnv.height);

        cnv.toImage(type, 0.92).then(img => {
          cnv.destroy();
          resolve(img);
        });
      });
    },

    /**
     * Triggers any assigned window resize handlers that have been added via
     * addResizeListener
     *
     * @param {Event} e
     * @return {void}
     */
    resizeHandlers(e) {
      this.getIsMobile();
      for (let i = 0, l = this.resizeListeners.length; i < l; ++i) {
        this.resizeListeners[i](e);
      }
    },

    /**
     * Scans images in canvas and warns about low dpi
     *
     * @returns {void}
     */
    warnAboutImageSize() {
      const cnv = this.getCanvas();

      if (!cnv) {
        return;
      }

      const bound = cnv.boundingBox;

      const images = cnv
        .getObjects()
        .filter(obj => obj?.className === 'Image' && obj.exportElement);

      if (images.length === 0) {
        return;
      }

      let hasLowResImages = false;

      images.forEach(image => {
        if (image.img.naturalWidth) {
          // if image was newly added to canvas
          const dpi =
            image.img.naturalWidth /
            (image.width / bound.pixelsPerCm / bound.cmToInch);
          if (dpi < 125) {
            hasLowResImages = true;
          }
        }
      });
      if (hasLowResImages) {
        snackbar('Low resolution images uploaded', false, 5000);
      }
    },

    /**
     * Performs some required initialisation commands to prepare the page for
     * Studio
     *
     * @return {void}
     */
    initialise() {
      if (this.$route.query.template) {
        this.loadTemplate(this.$route.query.template);
      }

      window.addEventListener('resize', this.resizeHandlers);
      this.resizeHandlers();

      if (this.designUrl) {
        const img = new Image();

        img.crossOrigin = 'anonymous';

        img.onload = () => {
          this.createImageElement(img);
        };

        img.src = this.designUrl;
      }
    },

    loadTemplate(hash) {
      this.axios
        .get('/omnis/v3/dashboard/studio/artwork/getTemplate', {
          params: {unique_hash: hash},
        })
        .then(({data}) => {
          if (data && data.content) {
            this.setVuexProperty('template', data);

            this.getCanvas().fromTemplate(JSON.parse(data.content));
            this.showGetStarted = false;
          }
        });
    },

    /**
     * Gets the current height of this component
     *
     * @return {Number}
     */
    getStudioComponentHeight() {
      if (this.$refs.container) {
        return (
          this.$refs.container.offsetHeight -
          (this.isMobile() ? this.getNavHeight() : 0)
        );
      }
      return 200;
    },

    /**
     * Updates the zoom level on the stage
     *
     * @return {void}
     */
    applyZoom() {
      const canvas = this.getCanvas();
      if (!canvas) {
        return;
      }

      const isCurrentlyZoomed =
        this.isZoomedIn || canvas.params.scale > this.minZoom + 0.001;

      if (!isCurrentlyZoomed) {
        this.zoom = canvas.zoomToFitBounds();
        this.isZoomedIn = true;
      } else {
        this.zoom = canvas.resetZoom();
        this.isZoomedIn = false;
      }
    },

    /**
     * Gets the current height of the nav bar
     *
     * @return {Number}
     */
    getNavHeight() {
      const navContainer = this.$refs.navbarContainer;
      if (navContainer) {
        const tabItems = navContainer.getElementsByClassName('tab-item');
        if (tabItems.length) {
          return navContainer.offsetHeight;
        }
      }
      return 60;
    },

    /**
     * Apply a colour filter to modify the HSV values of an OmniImage
     * element
     *
     * @param {Object} hsv New HSV values to apply to the image
     * @param {Boolean} closeColourPopup
     * @return {void}
     */
    applyColourFilter(hsv) {
      if (!this.activeEl) {
        this.updateBackgroundColour(hsv);
        return;
      }

      if (typeof hsv === 'string') {
        hsv = hexToHsv(hsv);
      }

      if (this._colourFilterTimeout) {
        window.clearTimeout(this._colourFilterTimeout);
      }

      this._colourFilterTimeout = window.setTimeout(
        () => {
          const activeElement = this.getActiveElement();
          if (activeElement && activeElement.className) {
            if (activeElement.className === 'Image') {
              if (
                !activeElement.colourIndex &&
                typeof activeElement.getImageMetaData === 'function'
              ) {
                activeElement.getImageMetaData();
              }

              if (!hsv) {
                activeElement.clearFilters();
                this.showColPalette = false;
              } else {
                this.recolourElement(activeElement, hsv);
              }

              activeElement.applyFilters();
            } else if (!hsv) {
              activeElement.color = '#000';
              this.showColPalette = false;
            } else {
              activeElement.color = hsvToHex(hsv);
            }
            this.getCanvas().render();
          }
        },
        /**
         * this means that you can only recolour a design 10 times per second
         * (Safari can't handle much more than that)
         */
        Date.now() % 100
      );
    },

    recolourElement(activeElement, hsv) {
      if (activeElement.colourIndex?.length === 1) {
        // if one colour then we can just change the entire image to another colour
        this.hardRecolour(activeElement, hsv);
      } else if (activeElement.averageHsv) {
        if (activeElement.averageHsv.s < 0.1) {
          if (
            activeElement.averageHsv.v < 0.1 ||
            activeElement.averageHsv.v > 0.9
          ) {
            // if very bright or very dark then we can just recolour the entire
            // image to a single colour
            this.hardRecolour(activeElement, hsv);
          } else {
            // if the saturation is low then we must saturate it before we recolour it
            this.saturateAndRecolour(activeElement, hsv);
          }
        } else {
          this.softRecolour(activeElement, hsv);
        }
      }
    },

    /**
     * Recolour the entire image to one flat hex value
     *
     * @param {*} modification
     * @param {*} hsv
     */
    hardRecolour(activeElement, hsv) {
      const modification = Object.assign(hsv);

      activeElement.addFilter('modifyHsv', {
        hsv: modification,
        mode: 'source-over',
      });
    },

    saturateAndRecolour(activeElement, hsv) {
      activeElement.addFilter('modifyHsv', {
        hsv: {
          s: 1,
          v: hsv.v,
        },
        mode: 'overlay',
        strength: 0.5,
      });

      activeElement.addFilter('recolour', {rgb: hsvToRgb(hsv), mode: 'hue'});
    },

    softRecolour(activeElement, hsv) {
      activeElement.addFilter('modifyHsv', {
        hsv: {
          s: hsv.s,
          v: hsv.v,
        },
        mode: 'overlay',
        strength: 0.5,
      });

      activeElement.addFilter('recolour', {rgb: hsvToRgb(hsv), mode: 'hue'});
    },

    /**
     * Changes the background colour of the stage
     *
     * @param {Object|string} colour Either and HSV object or Hex string
     */
    updateBackgroundColour(colour) {
      let hex;

      if (!colour) {
        this.getCanvas().params.background.fill = '#fff';
      } else if (typeof colour === 'string') {
        hex = colour;
      } else {
        hex = hsvToHex(colour);
      }

      this.getCanvas().params.background.fill = hex;

      this.getCanvas().render();
    },

    filterActiveElement() {
      this.filterableImage = this.activeEl.original_img;
      overlays.open('image-filter-popup-studio');
    },

    onFilterSave(img, filters) {
      this.activeEl.logFilters(filters);

      this.activeEl.changeImage(img, true);
      this.getCanvas().render();
    },

    onFilterPopupClosed() {
      this.filterableImage = null;
    },

    /**
     * Open a tooltip once and only once
     *
     * @param tooltip the tooltip to open
     * @param delay how long to wait before opening the tooltip
     * @param excludeBadge don't show the tooltip if the store owner has a badge
     * @param includeBadge only show the tooltip if the store owner has a badge
     *
     * NOTE: you shouldn't use the excludeBadge or includeBadge params directly
     * Use the alternate methods openTooltipOnceIfDoesNotHaveBadge and
     * openTooltipOnceIfHasBadge instead
     */
    openTooltipOnce(
      tooltip,
      delay = 100,
      excludeBadge = null,
      includeBadge = null
    ) {
      if (this.config.showFirstTimeTooltips === 'dashboard') {
        if (excludeBadge && Manufacturer.hasBadge(excludeBadge)) {
          return;
        }

        if (includeBadge && !Manufacturer.hasBadge(includeBadge)) {
          return;
        }
      }

      if (!this.getVuexProperty(`shownTooltip:${tooltip}`)) {
        setTimeout(() => {
          overlays.open(`tooltip-${tooltip}`);
          this.setVuexProperty(`shownTooltip:${tooltip}`, true);
        }, delay);
      }
    },

    openTooltipOnceIfDoesNotHaveBadge(tooltip, badge = null, delay = 100) {
      this.openTooltipOnce(tooltip, delay, badge);
    },

    openTooltipOnceIfHasBadge(tooltip, badge = null, delay = 100) {
      this.openTooltipOnce(tooltip, delay, null, badge);
    },

    onElementDeleted() {
      // noop
    },
  },

  mounted() {
    this.registeredVuexModule = true;
    this.registerVuexModule();

    if (this.canvas) {
      this.initialise();
    }

    if (this.defaultOptions) {
      this.setVuexProperty('defaultOptions', this.defaultOptions);
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.resizeHandlers);
  },
};
